import { filterDefinitions } from 'components/Filters/filterDefinitions'

const matchFilterValue = (name, availableValues) => {
  if (!name || !availableValues?.length) return null
  const normalized = name.trim().toLowerCase()

  const exact = availableValues.find(
    v => v.name?.toLowerCase() === normalized || v.value?.toLowerCase() === normalized
  )
  if (exact) return exact.id || exact.value

  if (normalized.length >= 4) {
    const partial = availableValues.find(
      v => v.name?.toLowerCase().includes(normalized) || normalized.includes(v.name?.toLowerCase())
    )
    if (partial) return partial.id || partial.value
  }

  return null
}

/**
 * Maps AI candidateListingArguments to URL filter params.
 */
export const mapCandidateListingArgumentsToFilterParams = (candidateListingArguments, resolvedFilters, filterValues) => {
  const params = new URLSearchParams()

  if (!candidateListingArguments) return params

  // candidateType -> candidateTypeId
  if (resolvedFilters?.candidateTypeId) {
    params.set('candidateTypeId', resolvedFilters.candidateTypeId)
  } else if (candidateListingArguments.candidateType) {
    const match = filterValues?.candidateTypes?.find(
      ct => ct.name?.toLowerCase() === candidateListingArguments.candidateType?.toLowerCase()
    )
    if (match) params.set('candidateTypeId', match.id || match.value)
  }

  // specialties -> specialtyIds
  if (resolvedFilters?.specialtyIds?.length > 0) {
    params.set('specialtyIds', resolvedFilters.specialtyIds.join('|'))
  } else if (candidateListingArguments.specialties?.length > 0) {
    const ids = candidateListingArguments.specialties
      .map(name => matchFilterValue(name, filterValues?.specialties))
      .filter(Boolean)
    if (ids.length > 0) params.set('specialtyIds', ids.join('|'))
  }

  // subspecialties -> subspecialtyIds
  if (resolvedFilters?.subspecialtyIds?.length > 0) {
    params.set('subspecialtyIds', resolvedFilters.subspecialtyIds.join('|'))
  }

  // states -> states (current location)
  if (resolvedFilters?.states?.length > 0) {
    params.set('states', resolvedFilters.states.join('|'))
  } else if (candidateListingArguments.states?.length > 0) {
    const ids = candidateListingArguments.states
      .map(name => matchFilterValue(name, filterValues?.states))
      .filter(Boolean)
    if (ids.length > 0) params.set('states', ids.join('|'))
  }

  // preferredStates -> preferredStates
  if (resolvedFilters?.preferredStates?.length > 0) {
    params.set('preferredStates', resolvedFilters.preferredStates.join('|'))
  } else if (candidateListingArguments.preferredStates?.length > 0) {
    const ids = candidateListingArguments.preferredStates
      .map(name => matchFilterValue(name, filterValues?.preferredStates))
      .filter(Boolean)
    if (ids.length > 0) params.set('preferredStates', ids.join('|'))
  }

  // licensedStates -> licensedStates
  if (resolvedFilters?.licensedStates?.length > 0) {
    params.set('licensedStates', resolvedFilters.licensedStates.join('|'))
  } else if (candidateListingArguments.licensedStates?.length > 0) {
    const ids = candidateListingArguments.licensedStates
      .map(name => matchFilterValue(name, filterValues?.licensedStates))
      .filter(Boolean)
    if (ids.length > 0) params.set('licensedStates', ids.join('|'))
  }

  // searchStatusTypes
  if (resolvedFilters?.searchStatusTypeIds?.length > 0) {
    params.set('searchStatusTypes', resolvedFilters.searchStatusTypeIds.join('|'))
  }

  // boardEligibilityTypes
  if (resolvedFilters?.boardEligibilityTypeIds?.length > 0) {
    params.set('boardEligibilityTypes', resolvedFilters.boardEligibilityTypeIds.join('|'))
  }

  // yearsExperienceTypes
  if (resolvedFilters?.yearsExperienceTypeIds?.length > 0) {
    params.set('yearsExperienceTypes', resolvedFilters.yearsExperienceTypeIds.join('|'))
  }

  // practiceTypes -> preferredPracticeTypes
  if (resolvedFilters?.practiceTypeIds?.length > 0) {
    params.set('preferredPracticeTypes', resolvedFilters.practiceTypeIds.join('|'))
  }

  // areaTypes -> preferredAreaTypes
  if (resolvedFilters?.areaTypeIds?.length > 0) {
    params.set('preferredAreaTypes', resolvedFilters.areaTypeIds.join('|'))
  }

  // visas -> requiredVisaIds
  if (resolvedFilters?.visaIds?.length > 0) {
    params.set('requiredVisaIds', resolvedFilters.visaIds.join('|'))
  }

  // languages
  if (resolvedFilters?.languageIds?.length > 0) {
    params.set('languages', resolvedFilters.languageIds.join('|'))
  }

  // preferredGlobalRegions
  if (resolvedFilters?.preferredGlobalRegionIds?.length > 0) {
    params.set('preferredGlobalRegions', resolvedFilters.preferredGlobalRegionIds.join('|'))
  }

  return params
}

const resolveFilterLabels = (valueString, valuesKey, filterValues) => {
  if (!filterValues || !valuesKey) return valueString

  const values = filterValues[valuesKey]
  if (!values || !Array.isArray(values)) return valueString

  const ids = valueString.split('|')
  const labels = ids.map(id => {
    const match = values.find(v => String(v.id || v.value) === String(id))
    return match?.name || id
  })
  return labels.join(', ')
}

/**
 * Builds a human-readable message describing filter changes.
 */
export const buildFilterChangeMessage = (previousParams, currentParams, filterValues) => {
  const changes = []

  const filterKeys = filterDefinitions
    .filter(fd => fd.queryStringKey && fd.isSearchParamVisible)
    .map(fd => ({ key: fd.queryStringKey, name: fd.displayName, valuesKey: fd.valuesResponseKey }))

  for (const { key, name, valuesKey } of filterKeys) {
    const prev = previousParams.get(key) || ''
    const curr = currentParams.get(key) || ''

    if (prev === curr) continue

    if (curr && !prev) {
      const labels = resolveFilterLabels(curr, valuesKey, filterValues)
      changes.push(`Added ${name}: ${labels}`)
    } else if (!curr && prev) {
      changes.push(`Removed ${name}`)
    } else {
      const labels = resolveFilterLabels(curr, valuesKey, filterValues)
      changes.push(`Changed ${name} to: ${labels}`)
    }
  }

  if (changes.length === 0) return null
  return changes.join('. ') + '.'
}

/**
 * Builds a summary of all active filters for the first AI message.
 */
export const buildActiveFiltersSummary = (searchParams, filterValues) => {
  const parts = []

  const filterKeys = filterDefinitions
    .filter(fd => fd.queryStringKey && fd.isSearchParamVisible)
    .map(fd => ({ key: fd.queryStringKey, name: fd.displayName, valuesKey: fd.valuesResponseKey }))

  for (const { key, name, valuesKey } of filterKeys) {
    const value = searchParams.get(key)
    if (!value) continue
    const labels = resolveFilterLabels(value, valuesKey, filterValues)
    parts.push(`${name}: ${labels}`)
  }

  if (parts.length === 0) return null
  return parts.join('. ') + '.'
}
